import { ReactElement } from 'react';
import { Option, Select } from '~/components/NewSelect';
import { DriConfigState } from '../types';
import { useTranslation } from 'react-i18next';
import {
  driApplicationCfgs, driApplicationOpts, driLayerOpts, driParityOpts, driProtocolsOpts, driStopBitsOpts, driVAVApplicationsOptions,
  vavFancoilValveTypes,
} from '~/helpers/driConfigOptions';
import { getKeyByValue } from '../../IntegrRealTime/DriContents';
import { Manufacturer } from './DriConfigManufacturer';
import {
  isAutomation, isFancoilVavAutomation, isSplitaoInverter, isVAV,
} from '~/helpers/driConfig';
import { ClearSelect, Input } from '~/components';
import SelectSearch, { fuzzySearch } from 'react-select-search';
import { Label } from '~/components/FormMachine/styles';
import { Flex } from 'reflexbox';
import { ClearSelectWrapper, StyledSearchInput } from '../styles';

interface AutomationProps {
  state: DriConfigState;
  setState: (newState: Partial<DriConfigState>) => void;
  render: () => void;
  resetCfgFields: () => void;
}

export function DriConfigAutomation({
  render, setState, state, resetCfgFields,
}: AutomationProps): ReactElement | null {
  const { t } = useTranslation();
  const vav = isVAV(state);

  const setDriApplication = (item?: {name: string, value: string}) => {
    resetCfgFields();

    if (item?.value === 'splitao-inverter') {
      state.driApplication = null;
      return;
    }

    state.driApplication = getKeyByValue(driApplicationOpts, item?.value) ?? null;
  };

  if (!isAutomation(state)) {
    return null;
  }

  async function onSelectMachine(item) {
    state.formData.MACHINE_ID_item = { MACHINE_NAME: item.name, MACHINE_ID: item.value, MACHINE_BRAND: item?.MACHINE_BRAND };
    if (isSplitaoInverter(state)) {
      verifyMachineBrand(item?.MACHINE_BRAND);
    }
    render();
  }

  function verifyMachineBrand(MACHINE_BRAND: string) {
    if (MACHINE_BRAND === 'hitachi') {
      const config = driApplicationCfgs['Splitão Inverter Hitachi'];
      state.driApplication = 'Splitão Inverter Hitachi';
      state.driSlaveId = '1';
      state.driStopBits = config.stopBits;
      state.driParity = config.parity;
      state.modbusBaudRate = config.modbusBaudRate;
      state.driSendInterval = '60';
      state.driLayer = config.serialMode;
      state.driProtocol = getKeyByValue(driProtocolsOpts, config.protocol) ?? null;
    } else if (MACHINE_BRAND === 'carrier') {
      const config = driApplicationCfgs['Splitão Inverter'];
      state.driApplication = 'Splitão Inverter';
      state.driProtocol = getKeyByValue(driProtocolsOpts, config.protocol) ?? null;
      state.modbusBaudRate = config.modbusBaudRate;
      state.driParity = config.parity;
      state.driLayer = config.serialMode;
      state.driStopBits = config.stopBits;
      state.driSendInterval = '60';
    }
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        <Select
          style={{ width: '200px' }}
          options={state.comboOpts.automation}
          value={state.automation}
          label={t('tipoDeEquipamento')}
          placeholder={t('selecionar')}
          onSelect={(item) => {
            state.automation = (item !== '') ? item : '';
            setDriApplication(item);
            render();
          }}
          propLabel="name"
        />
        {vav && (
          <Input
            style={{ width: '200px', height: '50px' }}
            label={t('ambienteMonitorado')}
            placeholder={t('digitar')}
            value={state.roomName}
            onChange={(event) => setState({ roomName: event.target.value })}
          />
        )}
        {isFancoilVavAutomation(state) && (
          <Select
            style={{ width: '200px' }}
            options={vavFancoilValveTypes}
            value={state.selectedValveModel}
            label={t('tipoDeAtuador')}
            placeholder={t('selecionar')}
            onSelect={(item) => {
              setState({ selectedValveModel: item });
            }}
            propLabel="name"
          />
        )}
        {(isSplitaoInverter(state) || isFancoilVavAutomation(state)) && (
          <>
            <Flex flexDirection="column" data-testid="machine-select">
              <StyledSearchInput>
                <div>
                  <Label>{t('maquina')}</Label>
                  <SelectSearch
                    label={t('maquina')}
                    placeholder={t('selecionar')}
                    search
                    filterOptions={fuzzySearch}
                    options={state.comboOpts?.machines.map((item) => ({ value: item.MACHINE_ID, name: item.MACHINE_NAME, MACHINE_BRAND: item.MACHINE_BRAND }))}
                    value={state.formData.MACHINE_ID_item?.MACHINE_ID.toString()}
                    onChange={(item, value) => { onSelectMachine(value); }}
                    disabled={!state.comboOpts?.machines?.length}
                  />
                </div>
              </StyledSearchInput>

              {state.formData.MACHINE_ID_item?.MACHINE_ID && (
              <ClearSelectWrapper>
                <ClearSelect
                  onClickClear={() => {
                    state.formData.MACHINE_ID_item = null;
                    render();
                  }}
                  value={state.formData.MACHINE_ID_item?.MACHINE_NAME}
                />
              </ClearSelectWrapper>
              )}
            </Flex>
          </>
        )}
      </div>
      {isFancoilVavAutomation(state) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          <Select
            style={{ width: '200px' }}
            label={t('fabricanteTermostato')}
            placeholder={t('selecionar')}
            options={state.comboOpts.thermManuf}
            value={state.thermManuf}
            onSelect={(item) => setState({ thermManuf: item })}
          />
          <Select
            style={{ width: '200px' }}
            label={t('modeloTermostato')}
            placeholder={t('selecionar')}
            options={driVAVApplicationsOptions}
            value={state.driVavFancoilModel}
            onSelect={(item) => {
              if (item) {
                const value = (item as Option).value;
                state.driVavFancoilModel = value;
                const defaultConfig = driApplicationCfgs[value];
                if (state.driApplication === 'Fancoil' && defaultConfig.application) {
                  const application = defaultConfig.application.split('-');
                  application[0] = 'fancoil';
                  defaultConfig.application = application.join('-');
                }
                if (defaultConfig) {
                  state.driProtocol = getKeyByValue(driProtocolsOpts, defaultConfig.protocol) || null;
                  state.modbusBaudRate = defaultConfig.modbusBaudRate;
                  state.driParity = getKeyByValue(driParityOpts, defaultConfig.parity) || null;
                  state.driLayer = getKeyByValue(driLayerOpts, defaultConfig.serialMode) || null;
                  state.driStopBits = getKeyByValue(driStopBitsOpts, defaultConfig.stopBits) || null;
                } else { resetCfgFields(); }
              } else {
                state.driVavFancoilModel = item;
              }
              render();
            }}
          />
          <Manufacturer
            state={state}
            setState={setState}
          />
        </div>
      )}
    </>
  );
}
